import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface AddressSelectProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  onSelect: (value: string) => void;
}

const AddressSelect: React.FC<AddressSelectProps> = ({
  id,
  label,
  value,
  options,
  placeholder = "Select...",
  disabled = false,
  loading = false,
  onSelect,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter((option) =>
    option.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const toggle = () => {
    if (disabled) return;
    setOpen((previous) => {
      if (previous) setSearch("");
      return !previous;
    });
  };

  return (
    <div className="manage-address-select" ref={wrapperRef}>
      <label className="manage-address-select-label" htmlFor={id}>
        {label}
      </label>
      <button
        id={id}
        type="button"
        className={`manage-address-select-trigger ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        disabled={disabled}
        onClick={toggle}
        aria-expanded={open}
      >
        <span
          className={
            value
              ? "manage-address-select-value"
              : "manage-address-select-placeholder"
          }
        >
          {value || placeholder}
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={open ? "rotate" : ""}
        />
      </button>

      {open && (
        <div className="manage-address-select-menu">
          <input
            type="text"
            className="manage-address-select-search"
            placeholder="Search..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
          {loading ? (
            <div className="manage-address-select-empty">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="manage-address-select-empty">No results</div>
          ) : (
            filtered.map((option) => {
              const active = option === value;
              return (
                <button
                  type="button"
                  key={option}
                  className={`manage-address-select-option ${active ? "selected" : ""}`}
                  onMouseDown={() => {
                    onSelect(option);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <span className="manage-address-select-option-copy">
                    <span>{option}</span>
                  </span>
                  {active && <Check size={15} strokeWidth={2.5} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AddressSelect;
